import type { ReactNode } from "react";

/**
 * Two-column settings row: title + description on the narrow left, the control
 * on the right. Keeps line length readable and gives settings pages a calm,
 * premium "panel" rhythm instead of full-width walls of text.
 *
 * Wrap a set of these in <SettingsPanel> for the bordered card look.
 */
export function SettingRow({
  title,
  description,
  children,
  wide,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Drop the readable max-width cap on the control column (lists, tables, JSON). */
  wide?: boolean;
}) {
  return (
    <div className="grid gap-3 border-t border-border-secondary py-7 first:border-t-0 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-12">
      <div className="md:pt-0.5">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {description && (
          <p className="mt-1.5 text-xs leading-relaxed text-text-tertiary">
            {description}
          </p>
        )}
      </div>
      <div className={`min-w-0 ${wide ? "" : "max-w-2xl"}`}>{children}</div>
    </div>
  );
}

/** Bordered card that hosts a stack of <SettingRow>s. */
export function SettingsPanel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border-primary bg-bg-primary px-6">
      {children}
    </div>
  );
}
